import React, { useState, useEffect } from 'react';
import { Bell, Send, Users, Filter as _Filter, Trash2 as _Trash2, TrendingUp, User, Target, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import axiosInstance, { API_URL as _API_URL } from '../../utils/axios.config';
import BackendHealthChecker from './BackendHealthChecker';

interface NotificationStats {
  total: number;
  read: number;
  unread: number;
  byType: Record<string, number>;
  byPriority: Record<string, number>;
  adminNotifications: number;
}

const AdminNotifications: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'send' | 'stats'>('send');
  const [sendType, setSendType] = useState<'user' | 'users' | 'all' | 'role' | 'custom'>('all');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<NotificationStats | null>(null);

  // Form data
  const [formData, setFormData] = useState({
    userId: '',
    userIds: '',
    role: 'user',
    title: '',
    message: '',
    type: 'announcement',
    priority: 'medium',
    actionUrl: '',
    imageUrl: '',
    // Filters for 'all' type
    filters: {
      gender: '',
      isPremium: undefined as boolean | undefined,
      isActive: true,
      isBlocked: false
    },
    // Targeting for 'custom' type
    targeting: {
      gender: '',
      ageMin: '',
      ageMax: '',
      isPremium: undefined as boolean | undefined,
      isActive: true,
      lastActiveDays: ''
    }
  });

  useEffect(() => {
    if (activeTab === 'stats') {
      fetchStats();
    }
  }, [activeTab]);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required. Please login again.');
        return;
      }

      const response = await axiosInstance.get('/admin/notifications/stats', {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data && response.data.data) {
        setStats(response.data.data);
      } else {
        // If no data yet, set default stats
        setStats({
          total: 0,
          read: 0,
          unread: 0,
          byType: {},
          byPriority: {},
          adminNotifications: 0
        });
      }
    } catch (error: any) {
      console.error('Error fetching stats:', error);
      
      // More detailed error handling
      if (error.response) {
        // Server responded with error
        toast.error(error.response.data?.message || 'Failed to fetch notification statistics');
      } else if (error.request) {
        // No response received
        toast.error('Cannot connect to server. Please check if backend is running on port 5001');
      } else {
        // Other errors
        toast.error('Failed to fetch notification statistics');
      }
      
      // Set default stats on error
      setStats({
        total: 0,
        read: 0,
        unread: 0,
        byType: {},
        byPriority: {},
        adminNotifications: 0
      });
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSendNotification = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        toast.error('Authentication required. Please login again.');
        return;
      }

      // Validate required fields
      if (!formData.title || !formData.message) {
        toast.error('Title and message are required');
        setLoading(false);
        return;
      }

      let endpoint = '';
      const payload: any = {
        title: formData.title,
        message: formData.message,
        type: formData.type,
        priority: formData.priority,
        actionUrl: formData.actionUrl,
        imageUrl: formData.imageUrl
      };

      switch (sendType) {
        case 'user':
          if (!formData.userId) {
            toast.error('User ID is required');
            setLoading(false);
            return;
          }
          endpoint = '/admin/notifications/send-to-user';
          payload.userId = formData.userId;
          break;

        case 'users':
          if (!formData.userIds) {
            toast.error('User IDs are required');
            setLoading(false);
            return;
          }
          endpoint = '/admin/notifications/send-to-users';
          payload.userIds = formData.userIds.split(',').map((id: string) => id.trim());
          break;

        case 'all':
          endpoint = '/admin/notifications/send-to-all';
          payload.filters = formData.filters;
          break;

        case 'role':
          endpoint = '/admin/notifications/send-by-role';
          payload.role = formData.role;
          break;

        case 'custom':
          endpoint = '/admin/notifications/send-custom';
          payload.targeting = {
            ...formData.targeting,
            ageMin: formData.targeting.ageMin ? parseInt(formData.targeting.ageMin) : undefined,
            ageMax: formData.targeting.ageMax ? parseInt(formData.targeting.ageMax) : undefined,
            lastActiveDays: formData.targeting.lastActiveDays ? parseInt(formData.targeting.lastActiveDays) : undefined
          };
          break;
      }

      const response = await axiosInstance.post(endpoint, payload);

      toast.success(response.data.message || 'Notification sent successfully!');
      
      // Reset form
      setFormData({
        ...formData,
        title: '',
        message: '',
        actionUrl: '',
        imageUrl: ''
      });
    } catch (error: any) {
      console.error('Error sending notification:', error);
      
      // More detailed error handling
      if (error.response) {
        // Server responded with error
        toast.error(error.response.data?.message || 'Failed to send notification');
      } else if (error.request) {
        // No response received
        toast.error('Cannot connect to server. Please check if backend is running on port 5001');
      } else {
        // Other errors
        toast.error('Failed to send notification');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl mb-2">Push Notifications Manager</h1>
        <p className="text-gray-600">Send push notifications to users</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 mb-6 border-b">
        <button
          onClick={() => setActiveTab('send')}
          className={`px-6 py-3 transition-colors ${
            activeTab === 'send'
              ? 'border-b-2 border-pink-500 text-pink-600'
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          <Send className="inline-block w-5 h-5 mr-2" />
          Send Notifications
        </button>
        <button
          onClick={() => setActiveTab('stats')}
          className={`px-6 py-3 transition-colors ${
            activeTab === 'stats'
              ? 'border-b-2 border-pink-500 text-pink-600'
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          <TrendingUp className="inline-block w-5 h-5 mr-2" />
          Statistics
        </button>
      </div>

      {activeTab === 'send' && (
        <div className="space-y-6">
          {/* Health Checker */}
          <BackendHealthChecker />

          {/* Send Type Selection */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl mb-4">Select Recipients</h2>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <button
                onClick={() => setSendType('user')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  sendType === 'user'
                    ? 'border-pink-500 bg-pink-50'
                    : 'border-gray-200 hover:border-pink-300'
                }`}
              >
                <User className="w-8 h-8 mx-auto mb-2 text-pink-500" />
                <div className="font-medium">Single User</div>
                <div className="text-sm text-gray-500">By User ID</div>
              </button>

              <button
                onClick={() => setSendType('users')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  sendType === 'users'
                    ? 'border-pink-500 bg-pink-50'
                    : 'border-gray-200 hover:border-pink-300'
                }`}
              >
                <Users className="w-8 h-8 mx-auto mb-2 text-pink-500" />
                <div className="font-medium">Multiple Users</div>
                <div className="text-sm text-gray-500">By User IDs</div>
              </button>

              <button
                onClick={() => setSendType('all')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  sendType === 'all'
                    ? 'border-pink-500 bg-pink-50'
                    : 'border-gray-200 hover:border-pink-300'
                }`}
              >
                <Bell className="w-8 h-8 mx-auto mb-2 text-pink-500" />
                <div className="font-medium">All Users</div>
                <div className="text-sm text-gray-500">Announcement</div>
              </button>

              <button
                onClick={() => setSendType('role')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  sendType === 'role'
                    ? 'border-pink-500 bg-pink-50'
                    : 'border-gray-200 hover:border-pink-300'
                }`}
              >
                <Users className="w-8 h-8 mx-auto mb-2 text-pink-500" />
                <div className="font-medium">By Role</div>
                <div className="text-sm text-gray-500">User/Premium/Marketing</div>
              </button>

              <button
                onClick={() => setSendType('custom')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  sendType === 'custom'
                    ? 'border-pink-500 bg-pink-50'
                    : 'border-gray-200 hover:border-pink-300'
                }`}
              >
                <Target className="w-8 h-8 mx-auto mb-2 text-pink-500" />
                <div className="font-medium">Custom</div>
                <div className="text-sm text-gray-500">Advanced Targeting</div>
              </button>
            </div>
          </div>

          {/* Recipient Fields */}
          {sendType === 'user' && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg mb-4">User Information</h3>
              <input
                type="text"
                placeholder="Enter User ID"
                value={formData.userId}
                onChange={(e) => handleInputChange('userId', e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
              />
            </div>
          )}

          {sendType === 'users' && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg mb-4">User IDs</h3>
              <textarea
                placeholder="Enter User IDs separated by commas"
                value={formData.userIds}
                onChange={(e) => handleInputChange('userIds', e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                rows={3}
              />
              <p className="text-sm text-gray-500 mt-2">Example: 507f1f77bcf86cd799439011, 507f191e810c19729de860ea</p>
            </div>
          )}

          {sendType === 'all' && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg mb-4">Filters (Optional)</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm mb-2">Gender</label>
                  <select
                    value={formData.filters.gender}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      filters: { ...prev.filters, gender: e.target.value }
                    }))}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                  >
                    <option value="">All Genders</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm mb-2">Premium Status</label>
                  <select
                    value={formData.filters.isPremium === undefined ? '' : formData.filters.isPremium.toString()}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      filters: { 
                        ...prev.filters, 
                        isPremium: e.target.value === '' ? undefined : e.target.value === 'true' 
                      }
                    }))}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                  >
                    <option value="">All Users</option>
                    <option value="true">Premium Only</option>
                    <option value="false">Free Only</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {sendType === 'role' && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg mb-4">Select Role</h3>
              <select
                value={formData.role}
                onChange={(e) => handleInputChange('role', e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
              >
                <option value="user">Regular Users</option>
                <option value="premium">Premium Users</option>
                <option value="marketing">Marketing Users</option>
              </select>
            </div>
          )}

          {sendType === 'custom' && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg mb-4">Advanced Targeting</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm mb-2">Gender</label>
                  <select
                    value={formData.targeting.gender}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      targeting: { ...prev.targeting, gender: e.target.value }
                    }))}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                  >
                    <option value="">All Genders</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm mb-2">Premium Status</label>
                  <select
                    value={formData.targeting.isPremium === undefined ? '' : formData.targeting.isPremium.toString()}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      targeting: { 
                        ...prev.targeting, 
                        isPremium: e.target.value === '' ? undefined : e.target.value === 'true' 
                      }
                    }))}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                  >
                    <option value="">All Users</option>
                    <option value="true">Premium Only</option>
                    <option value="false">Free Only</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm mb-2">Age Range (Min)</label>
                  <input
                    type="number"
                    placeholder="Minimum Age"
                    value={formData.targeting.ageMin}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      targeting: { ...prev.targeting, ageMin: e.target.value }
                    }))}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-2">Age Range (Max)</label>
                  <input
                    type="number"
                    placeholder="Maximum Age"
                    value={formData.targeting.ageMax}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      targeting: { ...prev.targeting, ageMax: e.target.value }
                    }))}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-2">Last Active (Days)</label>
                  <input
                    type="number"
                    placeholder="e.g., 30"
                    value={formData.targeting.lastActiveDays}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      targeting: { ...prev.targeting, lastActiveDays: e.target.value }
                    }))}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                  />
                  <p className="text-sm text-gray-500 mt-1">Users active in the last X days</p>
                </div>
              </div>
            </div>
          )}

          {/* Notification Content */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg mb-4">Notification Content</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm mb-2">Title *</label>
                <input
                  type="text"
                  placeholder="Enter notification title"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                  maxLength={100}
                />
              </div>

              <div>
                <label className="block text-sm mb-2">Message *</label>
                <textarea
                  placeholder="Enter notification message"
                  value={formData.message}
                  onChange={(e) => handleInputChange('message', e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                  rows={4}
                  maxLength={500}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm mb-2">Type</label>
                  <select
                    value={formData.type}
                    onChange={(e) => handleInputChange('type', e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                  >
                    <option value="announcement">Announcement</option>
                    <option value="promotion">Promotion</option>
                    <option value="alert">Alert</option>
                    <option value="update">Update</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm mb-2">Priority</label>
                  <select
                    value={formData.priority}
                    onChange={(e) => handleInputChange('priority', e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm mb-2">Action URL (Optional)</label>
                <input
                  type="text"
                  placeholder="/plans or /matches"
                  value={formData.actionUrl}
                  onChange={(e) => handleInputChange('actionUrl', e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                />
                <p className="text-sm text-gray-500 mt-1">Where users will be redirected when clicking the notification</p>
              </div>

              <div>
                <label className="block text-sm mb-2">Image URL (Optional)</label>
                <input
                  type="text"
                  placeholder="https://example.com/image.jpg"
                  value={formData.imageUrl}
                  onChange={(e) => handleInputChange('imageUrl', e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                />
              </div>
            </div>
          </div>

          {/* Send Button */}
          <div className="flex justify-end">
            <Button
              onClick={handleSendNotification}
              disabled={loading}
              className="px-8 py-3 bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-lg hover:from-pink-600 hover:to-rose-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {loading ? (
                <>
                  <span className="inline-block animate-spin mr-2">⏳</span>
                  Sending...
                </>
              ) : (
                <>
                  <Send className="inline-block w-5 h-5 mr-2" />
                  Send Notification
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {activeTab === 'stats' && stats && (
        <div className="space-y-6">
          {/* Overview Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Notifications</p>
                  <p className="text-2xl mt-1">{stats.total}</p>
                </div>
                <Bell className="w-10 h-10 text-pink-500" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Read</p>
                  <p className="text-2xl mt-1">{stats.read}</p>
                </div>
                <CheckCircle className="w-10 h-10 text-green-500" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Unread</p>
                  <p className="text-2xl mt-1">{stats.unread}</p>
                </div>
                <AlertCircle className="w-10 h-10 text-orange-500" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Admin Sent</p>
                  <p className="text-2xl mt-1">{stats.adminNotifications}</p>
                </div>
                <Send className="w-10 h-10 text-blue-500" />
              </div>
            </div>
          </div>

          {/* By Type */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg mb-4">Notifications by Type</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(stats.byType).map(([type, count]) => (
                <div key={type} className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600 capitalize">{type}</p>
                  <p className="text-xl mt-1">{count}</p>
                </div>
              ))}
            </div>
          </div>

          {/* By Priority */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg mb-4">Notifications by Priority</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(stats.byPriority).map(([priority, count]) => (
                <div key={priority} className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600 capitalize">{priority}</p>
                  <p className="text-xl mt-1">{count}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminNotifications;